"use client";

import React, { useEffect, useRef, useState, useCallback } from "react";
import { Play, Pause, Plus, Minus, Download } from "lucide-react";
import "./AudioMessage.css";

type Props = {
  src: string;
  mimeType?: string | null;
  isOwn?: boolean;
};

const speeds = [0.75, 1, 1.25] as const;
const DURATION_CACHE = new Map<string, number>();

const formatMMSS = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds < 0) return "00:00";
  const s = Math.floor(seconds);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
};

const AudioMessage: React.FC<Props> = ({ src, mimeType, isOwn = false }) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const barRef = useRef<HTMLDivElement | null>(null);

  // Drag state that persists outside React events
  const dragging = useRef(false);

  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [current, setCurrent] = useState(0);
  const [rateIdx, setRateIdx] = useState(1);

const resolvedType = mimeType && mimeType.startsWith("audio") ? mimeType : undefined;

  // ---- Duration fallback using Web Audio API (for streams that hide metadata)
  const computeDurationFallback = useCallback(async () => {
    if (DURATION_CACHE.has(src)) {
      const d = DURATION_CACHE.get(src)!;
      if (d > 0 && Number.isFinite(d)) {
        setDuration(d);
        return;
      }
    }
    try {
      const resp = await fetch(src);
      const blob = await resp.blob();
      const ab = await blob.arrayBuffer();
      const Ctor =
        (window as any).AudioContext || (window as any).webkitAudioContext;
      const ctx = new Ctor();
      // Safari prefers a copied buffer
      const audioBuf = await ctx.decodeAudioData(ab.slice(0));
      const d = audioBuf.duration;
      if (Number.isFinite(d) && d > 0) {
        DURATION_CACHE.set(src, d);
        setDuration(d);
      }
      ctx.close?.();
    } catch {
      // swallow silently — not critical
    }
  }, [src]);

  // ---- Wire native media events and attempt to resolve duration
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;

    setCurrent(0);
    setPlaying(!el.paused);
    setDuration(0);

    const trySetDurationFromTag = () => {
      const d1 =
        Number.isFinite(el.duration) && el.duration !== Infinity
          ? el.duration
          : 0;
      const d2 = el.seekable && el.seekable.length > 0 ? el.seekable.end(0) : 0;
      const d = Math.max(d1, d2);
      if (d > 0) setDuration(d);
    };

    const onLoadedMetadata = () => trySetDurationFromTag();
    const onDurationChange = () => trySetDurationFromTag();
    const onCanPlay = () => trySetDurationFromTag();
    const onCanPlayThrough = () => trySetDurationFromTag();
    const onTimeUpdate = () => setCurrent(el.currentTime || 0);
    const onEnded = () => setPlaying(false);

    el.addEventListener("loadedmetadata", onLoadedMetadata);
    el.addEventListener("durationchange", onDurationChange);
    el.addEventListener("canplay", onCanPlay);
    el.addEventListener("canplaythrough", onCanPlayThrough);
    el.addEventListener("timeupdate", onTimeUpdate);
    el.addEventListener("ended", onEnded);

    const t = window.setTimeout(() => {
      if (!duration) computeDurationFallback();
    }, 200);

    return () => {
      window.clearTimeout(t);
      el.removeEventListener("loadedmetadata", onLoadedMetadata);
      el.removeEventListener("durationchange", onDurationChange);
      el.removeEventListener("canplay", onCanPlay);
      el.removeEventListener("canplaythrough", onCanPlayThrough);
      el.removeEventListener("timeupdate", onTimeUpdate);
      el.removeEventListener("ended", onEnded);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, resolvedType, computeDurationFallback]);

  // ---- Seek helpers
  const seekToClientX = useCallback(
    (clientX: number) => {
      const el = audioRef.current;
      const bar = barRef.current;
      if (!el || !bar || !duration) return;

      const rect = bar.getBoundingClientRect();
      // Guard against zero width if layout is too tight
      const width = Math.max(rect.width, 1);
      const pct = Math.min(Math.max(0, (clientX - rect.left) / width), 1);
      el.currentTime = pct * duration;
      setCurrent(el.currentTime);
    },
    [duration]
  );

  // ---- Global (document-level) drag listeners so dragging works outside the bar
  useEffect(() => {
    const onDocPointerMove = (e: PointerEvent) => {
      if (!dragging.current) return;
      seekToClientX(e.clientX);
    };
    const onDocPointerUp = () => {
      dragging.current = false;
    };

    // Touch fallback for older browsers
    const onDocTouchMove = (e: TouchEvent) => {
      if (!dragging.current) return;
      if (e.touches && e.touches[0]) {
        // Prevent page scroll while scrubbing
        e.preventDefault?.();
        seekToClientX(e.touches[0].clientX);
      }
    };
    const onDocTouchEnd = () => {
      dragging.current = false;
    };

    document.addEventListener("pointermove", onDocPointerMove);
    document.addEventListener("pointerup", onDocPointerUp);
    document.addEventListener("touchmove", onDocTouchMove, { passive: false });
    document.addEventListener("touchend", onDocTouchEnd);

    return () => {
      document.removeEventListener("pointermove", onDocPointerMove);
      document.removeEventListener("pointerup", onDocPointerUp);
      document.removeEventListener("touchmove", onDocTouchMove);
      document.removeEventListener("touchend", onDocTouchEnd);
    };
  }, [seekToClientX]);

  // ---- Local bar events (start drag / click seek / keyboard)
  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    dragging.current = true;
    (e.currentTarget as HTMLDivElement).setPointerCapture?.(e.pointerId);
    seekToClientX(e.clientX);
  };

  // Click-to-seek (without drag)
  const onClickBar = (e: React.MouseEvent<HTMLDivElement>) => {
    seekToClientX(e.clientX);
  };

  // Keyboard accessibility
  const onProgressKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const el = audioRef.current;
    if (!el || !duration) return;
    if (e.key === "ArrowRight") {
      el.currentTime = Math.min(el.currentTime + 5, duration);
      setCurrent(el.currentTime);
    } else if (e.key === "ArrowLeft") {
      el.currentTime = Math.max(el.currentTime - 5, 0);
      setCurrent(el.currentTime);
    }
  };

  // ---- Play / Pause
  const togglePlay = useCallback(async () => {
    const el = audioRef.current;
    if (!el) return;

    if (playing) {
      el.pause();
      setPlaying(false);
      return;
    }

    el.playbackRate = speeds[rateIdx];

    try {
      await el.play();
      setPlaying(true);
      if (!duration) computeDurationFallback();
    } catch {
      // Autoplay blocked until user gesture — ignore
    }
  }, [playing, rateIdx, duration, computeDurationFallback]);

  // ---- Speed controls
  const setPlaybackRateIdx = (idx: number) => {
    const el = audioRef.current;
    const clamped = Math.min(Math.max(idx, 0), speeds.length - 1);
    if (el) el.playbackRate = speeds[clamped];
    setRateIdx(clamped);
  };
  const decRate = () => setPlaybackRateIdx(rateIdx - 1);
  const incRate = () => setPlaybackRateIdx(rateIdx + 1);

  // ---- Derived UI values
  const pct = duration ? (current / duration) * 100 : 0;
  const remaining = Math.max((duration || 0) - current, 0);

  return (
    <div className={`audio-wrap ${isOwn ? "audio-wrap-own" : "audio-wrap-received"}`}>
      <div className="audio-wrap-top">
<audio ref={audioRef} preload="metadata">
  {resolvedType
    ? <source src={src} type={resolvedType} />
    : <source src={src} /> }
</audio>

        <button
          className="am-btn am-play"
          onClick={togglePlay}
          aria-label={playing ? "Pause" : "Play"}
        >
          {playing ? <Pause size={18} /> : <Play size={18} />}
        </button>

        <div
          ref={barRef}
          className="am-progress"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(pct)}
          onPointerDown={onPointerDown}
          onClick={onClickBar}
          onKeyDown={onProgressKeyDown}
          tabIndex={0}
          title="Seek"
        >
          <div className="am-progress-fill" style={{ width: `${pct}%` }} />
        </div>

        <a
          className="am-btn am-download"
          href={src}
          download
          aria-label="Download audio"
          title="Download"
        >
          <Download size={18} />
        </a>
      </div>

      <div className="audio-wrap-bottom">
        <div className="am-rate">
          <button
            className="am-btn am-rate-btn"
            onClick={decRate}
            aria-label="Speed down"
            disabled={rateIdx <= 0}
            title="0.75×"
          >
            <Minus size={16} />
          </button>
          <span className="am-rate-label">{speeds[rateIdx].toFixed(2)}×</span>
          <button
            className="am-btn am-rate-btn"
            onClick={incRate}
            aria-label="Speed up"
            disabled={rateIdx >= speeds.length - 1}
            title="1.25×"
          >
            <Plus size={16} />
          </button>
        </div>

        <div className="am-time" dir="ltr" title="Remaining">
          {duration > 0 ? formatMMSS(remaining) : "00:00"}
        </div>
      </div>
    </div>
  );
};

export default AudioMessage;
